// Halaman "Cek Ongkir" reseller (`/account/cek-ongkir`) — helper MURNI.
//
// Layar ini MENGHITUNG SAJA: ia tak menyimpan pilihan, tak membuat kiriman,
// dan tak pernah menjadi angka yang ditagihkan. Reseller memakainya untuk
// menjawab "berapa ongkir ke Sidoarjo 2 kg?" SEBELUM ada order — pertanyaan
// yang sebelumnya hanya bisa dijawab dengan membuat keranjang lebih dulu.
//
// ── TIGA ATURAN YANG MENGIKAT MODUL INI ────────────────────────────────────
//
//  1. **ASAL PENGIRIMAN TIDAK PERNAH DATANG DARI SINI.** Ia dibaca route dari
//     setelan toko (`shipping_origin_*`) hasil resolusi Host, dan nilai origin
//     apa pun yang dikirim klien diabaikan. Layar hanya MENAMPILKANNYA sebagai
//     teks mati — lihat catatan panjang di `api/shipping/rates/route.ts`.
//
//  2. **DUA EKSPEDISI KHUSUS DIKECUALIKAN.** "Ongkir COD" & "Ambil di Tempat"
//     bukan tarif: keduanya kesepakatan yang nominalnya diisi tangan di alur
//     kirim (lihat `SPECIAL_EXPEDITIONS` di expeditions.ts). Menampilkannya di
//     layar "berapa ongkirnya" berarti menawarkan baris tanpa angka — atau,
//     lebih buruk, baris ber-angka yang dikarang. Penyaringnya di
//     `buildCekOngkirRows`, dan ia menyaring berdasarkan NAMA karena begitulah
//     kedua ekspedisi itu dikenali di seluruh repo.
//
//  3. **HANYA EKSPEDISI AKTIF.** Route sudah membatasi permintaan ke KODE
//     KURIR milik ekspedisi aktif (`readActiveCourierCodes`), tapi Biteship
//     memulangkan SELURUH layanan kurir itu — minta `jne` dan REG/YES/OKE
//     semuanya datang, walau toko cuma mengaktifkan satu baris "JNE REG".
//     `matchActiveExpedition` menutup celah itu di sisi tampilan.

import {
  courierLabel,
  formatDuration,
  rateLabel,
  MAX_SHIPMENT_WEIGHT_GRAM,
  type ShippingRate,
} from "@/lib/biteship";
import {
  manualRateKey,
  tierRangeLabel,
  type ManualRate,
} from "@/lib/expeditionTariff";
import {
  isSpecialExpedition,
  normalizeCourierCode,
  normalizeServiceCode,
  type Expedition,
} from "@/lib/expeditions";

// ─── 1. Berat yang diketik reseller ─────────────────────────────────────────

/**
 * Pintasan berat. Angka bulat yang benar-benar dipakai grosir fashion —
 * satuan gram karena itulah satuan yang dikirim ke route.
 */
export const CEK_ONGKIR_WEIGHT_PRESETS: readonly number[] = [
  1_000, 2_000, 3_000, 5_000, 10_000,
];

export type CekOngkirWeightProblem = "empty" | "zero" | "too-heavy";

/**
 * Apa yang salah dengan berat yang diketik — `null` bila tak ada.
 *
 * ⚠️ `zero` DIBEDAKAN dari `empty`, dan itu bukan kerapian. Route menjepit
 * berat yang tak terbaca ke minimum 1 kg (`weightBucketGram`), jadi mengirim 0
 * memulangkan tarif 1 kg **tanpa satu pun tanda bahwa itu terjadi** — persis
 * aturan "berat tidak diketahui → tidak ada angka sama sekali" yang dipegang
 * `RateOptions`. Batas atasnya sama dengan gerbang route
 * (`MAX_SHIPMENT_WEIGHT_GRAM`) supaya layar tak mengirim permintaan yang sudah
 * pasti dijawab 400.
 */
export function cekOngkirWeightProblem(
  gram: number | null | undefined
): CekOngkirWeightProblem | null {
  if (gram == null || !Number.isFinite(Number(gram))) return "empty";
  const g = Number(gram);
  if (g <= 0) return "zero";
  if (g > MAX_SHIPMENT_WEIGHT_GRAM) return "too-heavy";
  return null;
}

/** Pesan siap tampil untuk `cekOngkirWeightProblem`. */
export function cekOngkirWeightMessage(
  problem: CekOngkirWeightProblem
): string {
  if (problem === "empty") return "Isi berat paket dulu (dalam gram).";
  if (problem === "zero") return "Berat paket harus lebih dari 0 gram.";
  return "Berat paket di luar batas wajar.";
}

// ─── 2. Tarif kurir ⇄ ekspedisi aktif toko ──────────────────────────────────

/**
 * Hasil pencocokan satu tarif Biteship dengan daftar ekspedisi AKTIF toko.
 *
 *  • `exact`   — ada baris aktif dgn kode kurir DAN kode layanan yang sama.
 *  • `generic` — ada baris aktif dgn kode kurir sama & layanan `null`, yaitu
 *                baris yang mewakili kurirnya SECARA UMUM (perilaku sebelum
 *                mig 3950, masih sah). Seluruh layanan kurir itu ikut.
 *  • `none`    — toko tak menawarkan layanan ini. Barisnya DIBUANG.
 */
export type RateExpeditionMatch =
  | { kind: "exact"; expedition: Expedition }
  | { kind: "generic"; expedition: Expedition }
  | { kind: "none" };

/**
 * Tarif → baris ekspedisi aktif yang mewakilinya.
 *
 * ⚠️ SENGAJA TIDAK memakai `expeditionNameForRate`, walau pertanyaannya mirip.
 * Fungsi itu punya langkah ketiga — "baris aktif mana pun dgn kode kurir sama,
 * `sortOrder` terkecil" — yang dipertahankan supaya kiriman lama tak kehilangan
 * nama. Di sini langkah itu justru RACUN: toko yang cuma punya baris "JNE REG"
 * akan membuat tarif JNE YES tampil bernama "JNE REG" dengan HARGA YES. Di alur
 * kirim salah nama masih bisa dikoreksi admin sebelum uang berpindah; di layar
 * yang satu-satunya gunanya menjawab "berapa harganya", angka yang tertempel
 * pada nama yang salah adalah seluruh kerusakannya.
 *
 * Baris bernama ekspedisi khusus TIDAK PERNAH menang — alasan yang sama persis
 * dengan `expeditionNameForRate`: nama itu punya arti sendiri di hilir.
 */
export function matchActiveExpedition(
  list: ReadonlyArray<Expedition>,
  rate: Pick<ShippingRate, "courierCode" | "serviceCode">
): RateExpeditionMatch {
  const code = normalizeCourierCode(rate.courierCode);
  if (code === null) return { kind: "none" };
  const service = normalizeServiceCode(rate.serviceCode);

  const sameCourier = list
    .filter(
      (e) =>
        e.isActive &&
        normalizeCourierCode(e.biteshipCourierCode) === code &&
        e.name.trim() !== "" &&
        !isSpecialExpedition(e.name)
    )
    .sort(
      (a, b) =>
        a.sortOrder - b.sortOrder ||
        a.name.localeCompare(b.name, "id", { sensitivity: "base" })
    );

  const exact =
    service === null
      ? undefined
      : sameCourier.find(
          (e) => normalizeServiceCode(e.biteshipServiceCode) === service
        );
  if (exact) return { kind: "exact", expedition: exact };

  const generic = sameCourier.find(
    (e) => normalizeServiceCode(e.biteshipServiceCode) === null
  );
  if (generic) return { kind: "generic", expedition: generic };

  return { kind: "none" };
}

// ─── 3. Satu daftar siap render ─────────────────────────────────────────────

/**
 * Satu baris hasil cek ongkir — bentuk yang BENAR-BENAR dirender, tanpa sisa
 * bentuk asalnya.
 *
 * Tidak membawa `quoteId` dan itu disengaja: layar ini tak pernah memakai
 * kutipan (lihat `quote: false` di `fetchShippingRates`), dan field yang ada
 * tapi selalu kosong adalah undangan untuk memakainya suatu hari nanti.
 */
export type CekOngkirRow = {
  /** Kunci render — unik dalam satu daftar. */
  key: string;
  /** Judul baris: nama ekspedisi toko bila cocok persis, selain itu label kurir. */
  name: string;
  /** Baris kedua: layanan/estimasi (kurir) atau zona & tangga berat (manual). */
  detail: string;
  price: number;
  /** Kode kurir Biteship untuk logo. `null` = ekspedisi manual toko. */
  courierCode: string | null;
  /** Nama kurir apa adanya dari Biteship — cadangan inisial logo. */
  courierName: string;
  kind: "courier" | "manual";
};

/**
 * Gabungkan tarif kurir & tarif ekspedisi manual jadi SATU daftar terurut.
 *
 * Urutannya harga naik lalu nama — cermin `normalizeRates` & `readManualRates`.
 * Urutan yang bergantung urutan datang membuat dua pengecekan identik
 * menghasilkan dua daftar berbeda, dan pada layar pembanding harga itu terbaca
 * sebagai tarif yang berubah sendiri.
 */
export function buildCekOngkirRows(args: {
  rates: ReadonlyArray<ShippingRate>;
  manualRates: ReadonlyArray<ManualRate>;
  /** Ekspedisi AKTIF toko. Kosong = daftar belum tiba → tarif kurir ditahan. */
  expeditions: ReadonlyArray<Expedition>;
}): CekOngkirRow[] {
  const out: CekOngkirRow[] = [];

  for (const rate of args.rates) {
    const match = matchActiveExpedition(args.expeditions, rate);
    // Toko tak menawarkan layanan ini → dibuang. Termasuk saat daftar ekspedisi
    // belum tiba (`expeditions` kosong): menahan tarif sesaat jauh lebih ringan
    // daripada menjanjikan layanan yang ternyata sudah dinonaktifkan.
    if (match.kind === "none") continue;

    // `exact` = baris toko memang mewakili LAYANAN ini, jadi namanya dipakai
    // apa adanya — itu pula nama yang nanti dipilih reseller saat benar-benar
    // order, dan layanannya turun ke baris detail. `generic` jatuh ke label
    // kurir·layanan: baris toko hanya mewakili KURIRNYA, dan memakai namanya
    // akan menyamakan enam layanan berbeda jadi satu nama yang tak bisa
    // dibedakan — di situ layanannya sudah ada di judul, jadi detailnya cukup
    // estimasi saja.
    const exact = match.kind === "exact";
    out.push({
      key: `courier:${rate.courierCode}:${rate.serviceCode}`.toLowerCase(),
      name: exact ? match.expedition.name.trim() : rateLabel(rate),
      detail: cekOngkirCourierDetail(rate, exact),
      price: rate.price,
      courierCode: rate.courierCode,
      courierName: rate.courierName || courierLabel(rate.courierCode),
      kind: "courier",
    });
  }

  for (const manual of args.manualRates) {
    // ATURAN 2 — "Ongkir COD" & "Ambil di Tempat" tak pernah masuk daftar cek
    // ongkir. Baris DB bernama begitu bisa ada (admin bebas menamai
    // ekspedisinya), dan bila ia sempat diberi zona & tangga tarif ia akan
    // dihitung `readManualRates` seperti ekspedisi manual lain.
    if (isSpecialExpedition(manual.expeditionName)) continue;
    out.push({
      key: manualRateKey(manual),
      name: manual.expeditionName.trim(),
      detail: cekOngkirManualDetail(manual),
      price: manual.price,
      courierCode: null,
      courierName: manual.expeditionName.trim(),
      kind: "manual",
    });
  }

  out.sort(
    (a, b) => a.price - b.price || a.name.localeCompare(b.name, "id")
  );
  return out;
}

/**
 * "REG · Estimasi 1 - 2 hari" — layanan lebih dulu, estimasi bila ada.
 *
 * `withService: false` membuang nama layanannya karena judul barisnya SUDAH
 * memuatnya (`rateLabel`); mengulangnya di baris kedua hanya membuat "SiCepat ·
 * BEST" ditimpali "BEST · Estimasi 1 hari".
 */
function cekOngkirCourierDetail(rate: ShippingRate, withService: boolean): string {
  const service = withService
    ? rate.serviceName.trim() || rate.serviceCode.trim().toUpperCase()
    : "";
  const duration = formatDuration(rate.duration);
  return [service, duration ? `Estimasi ${duration}` : ""]
    .filter((s) => s !== "")
    .join(" · ");
}

/** "Jawa Timur · 2 kg – 3 kg" — zona yang menang lalu tangga beratnya. */
function cekOngkirManualDetail(manual: ManualRate): string {
  // Tarif GABUNGAN multigudang (mig 7200): tangga berat satu gudang tak
  // mewakili jumlahnya, jadi yang ditulis jumlah lokasinya — tanpa nama gudang
  // (layar ini juga dipakai katalog white-label).
  if (manual.legs && manual.legs.length > 1) {
    return [manual.zoneLabel.trim(), `gabungan ${manual.legs.length} lokasi`]
      .filter((s) => s !== "")
      .join(" · ");
  }
  const range = tierRangeLabel({
    id: manual.tierId,
    zoneId: manual.zoneId,
    minWeightGram: manual.minWeightGram,
    maxWeightGram: manual.maxWeightGram,
    price: manual.price,
    sortOrder: 0,
  });
  return [manual.zoneLabel.trim(), range].filter((s) => s !== "").join(" · ");
}

// ─── 4. Kenapa daftarnya kosong ─────────────────────────────────────────────

/**
 * Sebab daftar hasil kosong. Dibedakan karena KALIMATNYA berbeda, dan dua di
 * antaranya menuduh pihak yang salah bila tertukar.
 *
 *  • `feature-off`         — route menjawab `{enabled:false}`: toko belum
 *                            menyalakan cek ongkir / belum mengisi lokasi /
 *                            tak punya satu pun ekspedisi otomatis aktif.
 *  • `expeditions-pending` — route MEMULANGKAN tarif, tapi daftar ekspedisi
 *                            aktif belum tiba di klien sehingga semuanya
 *                            tersaring habis. Keadaan sementara, BUKAN fakta
 *                            tentang toko.
 *  • `no-route`            — tarifnya memang tak ada untuk rute ini.
 */
export type CekOngkirEmptyReason =
  | "feature-off"
  | "expeditions-pending"
  | "no-route";

/**
 * Kenapa `buildCekOngkirRows` memulangkan daftar kosong.
 *
 * ⚠️ `expeditions-pending` DIPERIKSA LEBIH DULU, dan itu inti fungsinya.
 * Server hanya menanyakan tarif untuk kode kurir milik ekspedisi AKTIF
 * (`readActiveCourierCodes`), jadi "route memulangkan tarif" sudah membuktikan
 * toko punya ekspedisi aktif. Bila daftar di klien saat itu masih kosong,
 * yang belum tiba adalah daftarnya — dan mengatakan "tak ada ekspedisi aktif
 * yang melayani rute ini" di situ adalah menuduh toko atas keterlambatan kita
 * sendiri.
 */
export function cekOngkirEmptyReason(args: {
  featureOff: boolean;
  /** Jumlah tarif kurir MENTAH dari route, sebelum disaring. */
  rateCount: number;
  /** Jumlah ekspedisi aktif yang sudah tiba di klien. */
  expeditionCount: number;
}): CekOngkirEmptyReason {
  if (args.rateCount > 0 && args.expeditionCount === 0) {
    return "expeditions-pending";
  }
  return args.featureOff ? "feature-off" : "no-route";
}

// ─── 5. Baris termurah ──────────────────────────────────────────────────────

/**
 * Kunci baris TERMURAH, atau `null` bila daftar kosong.
 *
 * Dipisah dari `buildCekOngkirRows` (bukan jadi flag pada barisnya) karena
 * "termurah" adalah sifat DAFTAR, bukan sifat baris: menyalinnya ke tiap baris
 * berarti dua sumber kebenaran yang bisa menyimpang saat daftarnya disaring
 * lagi di layar.
 *
 * Seri harga → baris PERTAMA yang menang. Daftarnya sudah terurut harga lalu
 * nama, jadi pemenang seri stabil antar pengecekan.
 */
export function cheapestRowKey(
  rows: ReadonlyArray<CekOngkirRow>
): string | null {
  let best: CekOngkirRow | null = null;
  for (const row of rows) {
    if (best === null || row.price < best.price) best = row;
  }
  return best?.key ?? null;
}
